import React, { useMemo } from "react";
import { useFela } from "react-fela";
import { useTheme } from "@/hooks/useTheme";

import { Icon, StyledIconWrapper } from "@/components/Icon";
import { Tooltip } from "@/components/Tooltip";
import { IColors } from "@/hooks/useTheme/types";
import { extensionNamesMap } from "@/containers/Converter/maps";
import { ConverterFeaturesTableHeader } from "./ConverterFeaturesTableHeader";
import {
  FeatureSupport,
  ILegendData,
  IFeatureItem,
  IConverterData,
  IFeatures,
  ISectionHeader,
  TableRow,
} from "../types";

interface IConverterFeaturesTable {
  data: IConverterData;
  legend: ILegendData;
}

interface ICellWithIcon {
  featureSupport: FeatureSupport;
  legend: ILegendData;
  colors: IColors;
}

export const ConverterFeaturesTable: React.FC<IConverterFeaturesTable> = ({
  data,
  legend,
}) => {
  const { css } = useFela();
  const { bp, colors } = useTheme();

  const mappedHeaders = useMemo(
    () => data.converters.map(converter => extensionNamesMap[converter]),
    [data.converters],
  );
  const headers = ["Feature", ...mappedHeaders];

  const tableBody = useMemo(() => {
    const res: TableRow[] = [];
    const convertersData = data.data;

    Object.keys(convertersData).forEach(header => {
      res.push({ feature: header as IFeatures });

      Object.keys(convertersData[header as ISectionHeader]).forEach(feature => {
        res.push({
          feature: feature as IFeatures,
          ...convertersData[header as ISectionHeader][feature],
        });
      });
    });
    return res;
  }, [data.data]);

  return (
    <>
      <div
        className={css({
          overflowX: "auto",
          [bp.FROM_DESKTOP]: { overflowX: "initial" },
        })}
      >
        <table
          cellSpacing="0"
          cellPadding="0"
          className={css({
            minWidth: 870,
            borderSpacing: "0 5px",
            width: "100%",
            borderCollapse: "collapse",
          })}
        >
          <ConverterFeaturesTableHeader header={headers} />

          <tbody>
            {tableBody.map((row: TableRow, i: number) => {
              const rowKeys = Object.keys(row);
              const isSectionHeader = rowKeys.length === 1;
              return (
                <tr
                  key={i}
                  className={css({
                    borderBottom: "1px solid #D1D5DB",
                    backgroundColor: isSectionHeader ? "#FBFBFF" : "inherit",
                  })}
                >
                  {rowKeys.map((key, i) => {
                    const isFirstCol = i === 0;
                    const feature = row[key as keyof TableRow] as
                      | string
                      | IFeatureItem;
                    return (
                      <td
                        key={i}
                        className={css({
                          width: isFirstCol ? "30%" : `${70 / rowKeys.length}%`,
                          fontSize: 14,
                          lineHeight: "24px",
                          padding: "13px 0",
                          textTransform: "capitalize",
                          textIndent: isFirstCol ? 15 : 0,
                          color: isSectionHeader
                            ? colors.GRAY
                            : colors.DARK_GREY,
                          fontWeight: isSectionHeader ? 700 : 400,
                        })}
                        colSpan={isSectionHeader ? 10 : 1}
                      >
                        {typeof feature === "string" ? (
                          feature
                        ) : feature?.tooltip ? (
                          <Tooltip
                            key={0}
                            text={feature?.tooltip}
                            tooltipStyles={{
                              width: 300,
                              bottom: 16,
                              transform: "translateX(calc(-50% + 8px))",
                              padding: "8px 12px",
                              backgroundColor: colors.YELLOW,
                              borderRadius: 4,
                              lineHeight: 0,
                              whiteSpace: "nowrap",
                            }}
                            tooltipArrowStyles={{
                              width: 16,
                              height: 16,
                              backgroundColor: colors.YELLOW,
                              borderRadius: 4,
                              transform: "rotate(45deg)",
                              left: "calc(50% - 8px)",
                              bottom: -6,
                            }}
                            tooltipTextStyles={{
                              fontSize: 12,
                              fontWeight: 700,
                              lineHeight: "16px",
                              letterSpacing: 0.25,
                              color: colors.DARK_GREY,
                              whiteSpace: "normal",
                              textTransform: "none",
                            }}
                          >
                            <CellWithIcon
                              featureSupport={feature?.support}
                              legend={legend}
                              colors={colors}
                            />
                          </Tooltip>
                        ) : (
                          <CellWithIcon
                            featureSupport={feature?.support}
                            legend={legend}
                            colors={colors}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

const CellWithIcon = ({ featureSupport, legend, colors }: ICellWithIcon) => (
  <StyledIconWrapper
    wrapperStyle={{
      width: 16,
      height: 16,
      lineHeight: "12px",
    }}
    customStyle={{
      stroke: featureSupport === "full" ? colors.GRADIENT_BLUE_DARK : "initial",
    }}
  >
    <Icon iconType={legend[featureSupport]?.icon} />
  </StyledIconWrapper>
);
